import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_KEY = process.env.REACT_APP_SUPABASE_PUBLISHABLE_KEY;

const HEADERS = {
  apikey: SUPABASE_KEY,
  Authorization: 'Bearer ' + SUPABASE_KEY
};

const JSON_HEADERS = {
  ...HEADERS,
  'Content-Type': 'application/json'
};

function normalizeInstitutionName(value) {
  return String(value || '')
    .trim()
    .replace(/\s+/g, ' ')
    .toLowerCase();
}

function isAcademicEmail(value) {
  const email = String(value || '').trim().toLowerCase();
  const match = email.match(/^[^\s@]+@([^\s@]+)$/);

  if (!match) return false;

  const domainParts = match[1].split('.');

  return (
    domainParts.includes('edu') ||
    domainParts.includes('ac')
  );
}

async function fetchInstitutions() {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/institutions?select=id,name&order=name.asc`,
    { headers: HEADERS }
  );

  if (!response.ok) {
    throw new Error(
      `Could not load institutions (${response.status}).`
    );
  }

  return response.json();
}

async function findExistingInstructor(email) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructors?email=eq.${encodeURIComponent(email)}&select=id&limit=1`,
    { headers: HEADERS }
  );

  if (!response.ok) {
    throw new Error(
      `Could not check instructor accounts (${response.status}).`
    );
  }

  const rows = await response.json();
  return Array.isArray(rows) && rows.length > 0;
}

async function findPendingApplication(email) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_applications?email=eq.${encodeURIComponent(email)}&select=id&limit=1`,
    { headers: HEADERS }
  );

  if (!response.ok) {
    throw new Error(
      `Could not check existing applications (${response.status}).`
    );
  }

  const rows = await response.json();
  return Array.isArray(rows) && rows.length > 0;
}

async function submitApplication(data) {
  const response = await fetch(
    `${SUPABASE_URL}/rest/v1/instructor_applications`,
    {
      method: 'POST',
      headers: JSON_HEADERS,
      body: JSON.stringify(data)
    }
  );

  if (!response.ok) {
    const detail = await response.text();

    throw new Error(
      `Application submission failed (${response.status}): ${detail}`
    );
  }
}

export default function InstructorApplication() {
  const navigate = useNavigate();

  const [institutions, setInstitutions] = useState([]);
  const [loadingInstitutions, setLoadingInstitutions] =
    useState(true);

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [institutionName, setInstitutionName] =
    useState('');
  const [position, setPosition] = useState('');
  const [useDescription, setUseDescription] =
    useState('');

  const [website, setWebsite] = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    fetchInstitutions()
      .then(rows => {
        if (!cancelled) {
          setInstitutions(
            Array.isArray(rows) ? rows : []
          );
        }
      })
      .catch(err => {
        console.error(
          'Institution list load failed:',
          err
        );
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingInstitutions(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const exactInstitution = useMemo(() => {
    const normalized =
      normalizeInstitutionName(institutionName);

    if (!normalized) return null;

    return (
      institutions.find(
        institution =>
          normalizeInstitutionName(institution.name) ===
          normalized
      ) || null
    );
  }, [institutionName, institutions]);

  async function handleSubmit(event) {
    event.preventDefault();
    setError('');

    const cleanEmail =
      String(email || '').trim().toLowerCase();
    const cleanName = String(name || '').trim();
    const cleanInstitution =
      String(institutionName || '')
        .trim()
        .replace(/\s+/g, ' ');
    const cleanPosition =
      String(position || '').trim();
    const cleanUseDescription =
      String(useDescription || '').trim();

    if (
      !cleanEmail ||
      !cleanName ||
      !cleanInstitution ||
      !cleanPosition ||
      !cleanUseDescription
    ) {
      setError('All fields are required.');
      return;
    }

    if (!isAcademicEmail(cleanEmail)) {
      setError(
        'Please use an academic email address containing an .edu or .ac domain.'
      );
      return;
    }

    if (website) {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);

    try {
      const alreadyInstructor =
        await findExistingInstructor(cleanEmail);

      if (alreadyInstructor) {
        setError(
          'An instructor account already exists for this email address. Please return to the login page.'
        );
        return;
      }

      const alreadyPending =
        await findPendingApplication(cleanEmail);

      if (alreadyPending) {
        setError(
          'An application has already been submitted for this email address.'
        );
        return;
      }

      await submitApplication({
        email: cleanEmail,
        name: cleanName,
        institution_id:
          exactInstitution?.id || null,
        institution_name:
          exactInstitution?.name ||
          cleanInstitution,
        position: cleanPosition,
        use_description: cleanUseDescription
      });

      setSubmitted(true);
    } catch (err) {
      console.error(
        'Instructor application failed:',
        err
      );

      setError(
        err?.message ||
          'Could not submit your application. Please try again.'
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) {
    return (
      <div style={styles.wrap}>
        <div style={styles.card}>
          <h1 style={styles.title}>
            Application submitted
          </h1>

          <p style={styles.sub}>
            Thank you. You’ll receive an email if your
            instructor account is approved.
          </p>

          <button
            type="button"
            style={styles.btn}
            onClick={() => navigate('/cms')}
          >
            Return to Instructor Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.wrap}>
      <div style={styles.card}>
        <h1 style={styles.title}>
          Apply for an Instructor Account
        </h1>

        <p style={styles.sub}>
          Instructor accounts are available to academics
          who would like to use the Instructor System in
          their teaching.
        </p>

        {error && (
          <div style={styles.error}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <label style={styles.label}>
            Academic email address
          </label>
          <input
            style={styles.input}
            type="email"
            value={email}
            onChange={event =>
              setEmail(event.target.value)
            }
            placeholder="name@university.edu"
            autoComplete="email"
            required
          />
          <div style={styles.help}>
            Please use an institutional academic address
            containing an .edu or .ac domain.
          </div>

          <label style={styles.label}>
            Name
          </label>
          <input
            style={styles.input}
            type="text"
            value={name}
            onChange={event =>
              setName(event.target.value)
            }
            placeholder="Full name"
            autoComplete="name"
            required
          />

          <label style={styles.label}>
            Institution
          </label>
          <input
            style={styles.input}
            type="text"
            value={institutionName}
            onChange={event =>
              setInstitutionName(event.target.value)
            }
            placeholder="Start typing your institution"
            list="instructor-application-institutions"
            autoComplete="organization"
            required
          />
          <datalist id="instructor-application-institutions">
            {institutions.map(institution => (
              <option
                key={institution.id}
                value={institution.name}
              />
            ))}
          </datalist>
          <div style={styles.help}>
            {loadingInstitutions
              ? 'Loading existing institutions...'
              : exactInstitution
                ? 'Existing institution selected.'
                : institutionName.trim()
                  ? 'If your institution is not already listed, it will be reviewed before your account is approved.'
                  : 'Select an existing institution if it appears, or enter a new one.'}
          </div>

          <label style={styles.label}>
            Position
          </label>
          <input
            style={styles.input}
            type="text"
            value={position}
            onChange={event =>
              setPosition(event.target.value)
            }
            placeholder="e.g. Assistant Professor"
            autoComplete="organization-title"
            required
          />

          <label style={styles.label}>
            Tell us a little about how you’d like to use
            the Instructor System
          </label>
          <textarea
            style={styles.textarea}
            value={useDescription}
            onChange={event =>
              setUseDescription(event.target.value)
            }
            placeholder="For example, the courses you teach and the parts of the system you’re interested in using."
            required
          />

          <div style={styles.honeypot} aria-hidden="true">
            <label>
              Website
              <input
                type="text"
                tabIndex="-1"
                autoComplete="off"
                value={website}
                onChange={event =>
                  setWebsite(event.target.value)
                }
              />
            </label>
          </div>

          <button
            style={styles.btn}
            disabled={submitting}
          >
            {submitting
              ? 'Submitting...'
              : 'Submit Application'}
          </button>

          <button
            type="button"
            style={styles.secondaryBtn}
            onClick={() => navigate('/cms')}
            disabled={submitting}
          >
            ← Back to Instructor Login
          </button>
        </form>
      </div>
    </div>
  );
}

const styles = {
  wrap: {
    minHeight: '100vh',
    background: '#f4f6f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    boxSizing: 'border-box'
  },
  card: {
    background: 'white',
    borderRadius: '12px',
    padding: '36px',
    width: '100%',
    maxWidth: '560px',
    boxShadow: '0 6px 20px rgba(0,0,0,0.08)'
  },
  title: {
    margin: '0 0 10px',
    fontSize: '22px',
    color: '#1f2933'
  },
  sub: {
    margin: '0 0 24px',
    color: '#66788a',
    fontSize: '14px',
    lineHeight: '1.55'
  },
  error: {
    background: '#faeaea',
    border: '1px solid #e8bcbc',
    borderRadius: '8px',
    padding: '10px 14px',
    color: '#a12a2a',
    fontSize: '14px',
    marginBottom: '16px'
  },
  label: {
    display: 'block',
    fontWeight: '700',
    fontSize: '13px',
    marginBottom: '6px',
    color: '#1f2933'
  },
  input: {
    width: '100%',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '15px',
    marginBottom: '6px',
    boxSizing: 'border-box',
    background: 'white'
  },
  textarea: {
    width: '100%',
    minHeight: '130px',
    resize: 'vertical',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '10px 12px',
    fontSize: '15px',
    lineHeight: '1.5',
    marginBottom: '18px',
    boxSizing: 'border-box',
    fontFamily: 'inherit'
  },
  help: {
    color: '#66788a',
    fontSize: '12px',
    lineHeight: '1.4',
    marginBottom: '16px'
  },
  btn: {
    width: '100%',
    background: '#215b7d',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '11px',
    fontSize: '15px',
    fontWeight: '600',
    cursor: 'pointer',
    marginBottom: '10px'
  },
  secondaryBtn: {
    width: '100%',
    background: 'transparent',
    color: '#66788a',
    border: '1px solid #d7dee5',
    borderRadius: '8px',
    padding: '10px',
    fontSize: '14px',
    cursor: 'pointer'
  },
  honeypot: {
    position: 'absolute',
    left: '-10000px',
    width: '1px',
    height: '1px',
    overflow: 'hidden'
  }
};